/**
 * REST API Error Response Parser
 * Handles structured error responses from REST APIs
 */

interface RestErrorDetail {
  type: string;
  loc: string[];
  msg: string;
  input?: unknown;
}

interface RestErrorResponse {
  detail?: RestErrorDetail[];
}

/**
 * Parse REST API error response and extract user-friendly message
 */
export const parseRestApiError = (error: unknown): string => {
  // Default error message
  let errorMessage = "REST API request failed";

  try {
    if (error instanceof Error) {
      // Check if error message contains JSON response
      const match = error.message.match(/(\{.*\})/);
      if (match) {
        try {
          const errorData = JSON.parse(match[1]) as RestErrorResponse;
          if (errorData.detail && Array.isArray(errorData.detail) && errorData.detail.length > 0) {
            // Extract field name and message from validation error
            const firstError = errorData.detail[0];
            const fieldName = firstError.loc?.join('.') || 'Field';
            const message = firstError.msg || 'Invalid input';
            return `${fieldName}: ${message}`;
          }
        } catch {
          // If JSON parsing fails, fall through to default handling
        }
      }

      // Check if the error message itself is a JSON string
      try {
        const errorData = JSON.parse(error.message) as RestErrorResponse;
        if (errorData.detail && Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          const fieldName = firstError.loc?.join('.') || 'Field';
          const message = firstError.msg || 'Invalid input';
          return `${fieldName}: ${message}`;
        }
      } catch {
        // Not JSON, use original error message
        return error.message;
      }

      // Return original error message if not structured
      return error.message;
    }

    // Handle non-Error objects
    if (typeof error === 'string') {
      try {
        const errorData = JSON.parse(error) as RestErrorResponse;
        if (errorData.detail && Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          const fieldName = firstError.loc?.join('.') || 'Field';
          const message = firstError.msg || 'Invalid input';
          return `${fieldName}: ${message}`;
        }
      } catch {
        return error;
      }
    }

    // Handle object errors
    if (typeof error === 'object' && error !== null) {
      const errorData = error as RestErrorResponse;
      if (errorData.detail && Array.isArray(errorData.detail) && errorData.detail.length > 0) {
        const firstError = errorData.detail[0];
        const fieldName = firstError.loc?.join('.') || 'Field';
        const message = firstError.msg || 'Invalid input';
        return `${fieldName}: ${message}`;
      }
    }

  } catch (parseError) {
    // If parsing fails, return default message
    console.warn('Failed to parse REST API error:', parseError);
  }

  return errorMessage;
};

/**
 * Extract all error messages from REST API error response
 */
export const getAllRestApiErrors = (error: unknown): string[] => {
  const messages: string[] = [];

  try {
    let errorData: RestErrorResponse | null = null;

    if (error instanceof Error) {
      // Try to parse JSON from error message
      const match = error.message.match(/(\{.*\})/);
      if (match) {
        try {
          errorData = JSON.parse(match[1]) as RestErrorResponse;
        } catch {
          try {
            errorData = JSON.parse(error.message) as RestErrorResponse;
          } catch {
            return [error.message];
          }
        }
      } else {
        try {
          errorData = JSON.parse(error.message) as RestErrorResponse;
        } catch {
          return [error.message];
        }
      }
    } else if (typeof error === 'string') {
      try {
        errorData = JSON.parse(error) as RestErrorResponse;
      } catch {
        return [error];
      }
    } else if (typeof error === 'object' && error !== null) {
      errorData = error as RestErrorResponse;
    }

    if (errorData?.detail && Array.isArray(errorData.detail)) {
      errorData.detail.forEach(detail => {
        const fieldName = detail.loc?.join('.') || 'Field';
        const message = detail.msg || 'Invalid input';
        messages.push(`${fieldName}: ${message}`);
      });
    }

  } catch (parseError) {
    console.warn('Failed to parse REST API errors:', parseError);
  }

  return messages.length > 0 ? messages : ['REST API request failed'];
};
